import React, {useEffect, useState} from 'react';
import {DatePicker, Form, Input, InputNumber, message, Modal, Tooltip, Upload} from 'antd';
import {PlusOutlined, QuestionCircleOutlined} from '@ant-design/icons';
import {CaseItem} from "../../types/CardItems";
import {UploadChangeParam, UploadFile} from 'antd/lib/upload/interface';
import {RcFile} from 'antd/lib/upload';
import dayjs from 'dayjs';

const API_BASE_URL = 'https://signed-url.meetyifan.workers.dev';

interface SignedUrlResponse {
    data: string;
}

const getOssSigned = async (): Promise<SignedUrlResponse> => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/palm/signed-url`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Failed to get signed URL');
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting signed URL:', error);
        throw error;
    }
};

interface CustomUploadFile extends UploadFile {
    url?: string;
    thumbUrl?: string;
}

interface CaseFormProps {
    visible: boolean;
    onCancel: () => void;
    onSubmit: (values: Partial<CaseItem>) => void;
    initialValues?: Partial<CaseItem>;
    loading?: boolean;
}

const CaseForm: React.FC<CaseFormProps> = ({
                                               visible,
                                               onCancel,
                                               onSubmit,
                                               initialValues,
                                               loading
                                           }) => {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState<CustomUploadFile[]>(() => {
        if (initialValues?.images) {
            return initialValues.images.map((url, index) => ({
                uid: `-${index}`,
                name: `image-${index}`,
                status: 'done',
                url: url,
                thumbUrl: url,
            }));
        }
        return [];
    });

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');

    useEffect(() => {
        if (initialValues) {
            form.setFieldsValue(initialValues);
            if (initialValues.images) {
                setFileList(initialValues.images.map((url, index) => ({
                    uid: `-${index}`,
                    name: `image-${index}`,
                    status: 'done',
                    url: url,
                    thumbUrl: url,
                })));
            }
        }
    }, [initialValues, form]);

    const stripBase64Prefix = (base64String: string): string => {
        return base64String.replace(/^data:image\/\w+;base64,/, '');
    };

    const uploadImageToStorage = async (base64Image: string): Promise<string> => {
        try {
            const response = await getOssSigned();
            const signedUrl = response.data;

            const binaryString = window.atob(stripBase64Prefix(base64Image));
            const arrayBuffer = new ArrayBuffer(binaryString.length);
            const uint8Array = new Uint8Array(arrayBuffer);

            for (let i = 0; i < binaryString.length; i++) {
                uint8Array[i] = binaryString.charCodeAt(i);
            }

            const blob = new Blob([uint8Array], {type: 'image/jpeg'});
            const file = new File([blob], 'image.jpg', {type: 'image/jpeg'});

            const uploadResponse = await fetch(signedUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': 'image/jpeg'
                }
            });

            if (uploadResponse.ok) {
                return signedUrl.split('?')[0];
            }
            throw new Error('Upload failed');
        } catch (error) {
            console.error('Error uploading image:', error);
            throw error;
        }
    };

    const handleUpload = async (file: RcFile): Promise<string> => {
        try {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);

                reader.onload = async () => {
                    try {
                        const base64Image = reader.result as string;
                        const imageUrl = await uploadImageToStorage(base64Image);
                        resolve(imageUrl);
                    } catch (error) {
                        reject(error);
                        message.error('Upload failed');
                    }
                };
                reader.onerror = reject;
            });
        } catch (error) {
            console.error('Error handling upload:', error);
            message.error('Upload failed');
            throw error;
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setFileList([]);
        setPreviewOpen(false);
        setPreviewImage('');
        onCancel();
    };

    const handleSubmit = async (values: Partial<CaseItem>) => {
        await onSubmit(values);
        form.resetFields();
        setFileList([]);
        setPreviewOpen(false);
        setPreviewImage('');
    };

    const handleChange = async (info: UploadChangeParam<CustomUploadFile>) => {
        let newFileList = [...info.fileList];

        newFileList = newFileList.map(file => {
            if (file.status === 'done' && file.response) {
                return {
                    ...file,
                    url: file.response.url,
                    thumbUrl: file.response.url,
                };
            }
            return file;
        });

        setFileList(newFileList);

        const urls = newFileList
            .filter(file => file.status === 'done')
            .map(file => file.url)
            .filter((url): url is string => url !== undefined);

        form.setFieldsValue({images: urls});
    };

    const handlePreview = (file: CustomUploadFile) => {
        setPreviewImage(file.url || file.thumbUrl || '');
        setPreviewOpen(true);
    };

    return (
        <>
            <Modal
                title={initialValues ? "Edit Case" : "Add New Case"}
                visible={visible}
                onCancel={handleCancel}
                onOk={() => form.submit()}
                confirmLoading={loading}
                className="case-form-modal"
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={initialValues}
                    onFinish={handleSubmit}
                >
                    <Form.Item label="Title (English)" name={['title', 'en']} rules={[{required: true}]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item label="Title (Chinese)" name={['title', 'zh']} rules={[{required: true}]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item
                        label={
                            <span>Show Date&nbsp;
                                <Tooltip
                                    title="This date will affect the sorting order of cases"><QuestionCircleOutlined
                                    style={{color: '#999'}}/></Tooltip></span>}
                        name="showTime"
                        rules={[{required: true, message: 'Please select show date'}]}
                        getValueProps={(value) => ({
                            value: value ? dayjs(value) : null
                        })}
                        getValueFromEvent={(date) => date?.valueOf() || null}>
                        <DatePicker
                            className="w-full"
                            format="YYYY-MM-DD"
                        />
                    </Form.Item>

                    <Form.Item label="Client (English)" name={['client', 'en']} rules={[{required: true}]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item label="Client (Chinese)" name={['client', 'zh']} rules={[{required: true}]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item label="Exhibition Area (m²)" name="exhibitionArea" rules={[{required: true}]}>
                        <InputNumber min={1} className="w-full"/>
                    </Form.Item>
                    <Form.Item label="Location (English)" name={['exhibitionLocation', 'en']}
                               rules={[{required: true}]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item label="Location (Chinese)" name={['exhibitionLocation', 'zh']}
                               rules={[{required: true}]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item
                        label="Images"
                        name="images"
                        rules={[{required: true, message: 'Please upload at least one image'}]}
                    >
                        <Upload
                            listType="picture-card"
                            fileList={fileList}
                            onChange={handleChange}
                            onPreview={handlePreview}
                            customRequest={async ({file, onSuccess, onError}) => {
                                try {
                                    const url = await handleUpload(file as RcFile);
                                    onSuccess?.({url});
                                } catch (error) {
                                    onError?.(error as Error);
                                }
                            }}
                        >
                            {fileList.length >= 8 ? null : (
                                <div>
                                    <PlusOutlined/>
                                    <div style={{marginTop: 8}}>Upload</div>
                                </div>
                            )}
                        </Upload>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                visible={previewOpen}
                footer={null}
                onCancel={() => setPreviewOpen(false)}
            >
                <img alt="preview" style={{width: '100%'}} src={previewImage}/>
            </Modal>
        </>
    );
};

export default CaseForm;
